import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPremium: boolean;
  premiumType: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isAdmin: boolean;
  username: string | null;
  avatarUrl: string | null;
  refreshProfile: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  setPremiumOverride: (val: boolean | null, type?: string | null) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isPremium: false,
  premiumType: null,
  subscriptionEnd: null,
  cancelAtPeriodEnd: false,
  isAdmin: false,
  username: null,
  avatarUrl: null,
  refreshProfile: async () => {},
  checkSubscription: async () => {},
  setPremiumOverride: () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [dbPremium, setDbPremium] = useState(false);
  const [premiumType, setPremiumType] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [premiumOverride, setPremiumOverride] = useState<boolean | null>(null);
  const [premiumTypeOverride, setPremiumTypeOverride] = useState<string | null>(null);

  // The effective premium status: admin override wins if set, otherwise DB value
  const isPremium = premiumOverride !== null ? premiumOverride : dbPremium;
  const effectivePremiumType = premiumOverride !== null ? premiumTypeOverride : premiumType;
  const effectiveSubscriptionEnd = premiumOverride !== null
    ? (premiumTypeOverride === "monthly" ? new Date(Date.now() + 30 * 86400000).toISOString() : null)
    : subscriptionEnd;

  const handleSetPremiumOverride = (val: boolean | null, type?: string | null) => {
    // Only allow override in-memory (admin testing only, not persisted to localStorage)
    setPremiumOverride(val);
    const overrideType = type !== undefined ? type : null;
    setPremiumTypeOverride(overrideType);
    localStorage.setItem("ayeris_premium", String(val !== null ? val : dbPremium));
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch profile from DB when user changes
  const checkSubscription = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (!error && data) {
        setDbPremium(data.subscribed ?? false);
        setPremiumType(data.premium_type ?? null);
        setSubscriptionEnd(data.subscription_end ?? null);
        setCancelAtPeriodEnd(data.cancel_at_period_end ?? false);
        localStorage.setItem("ayeris_premium", String(data.subscribed ?? false));
      }
    } catch {}
  };

  const fetchProfile = async () => {
    if (!user) {
      setDbPremium(false);
      setPremiumType(null);
      setUsername(null);
      setAvatarUrl(null);
      setIsAdmin(false);
      return;
    }
    const { data } = await (supabase
      .from("profiles") as any)
      .select("is_premium, premium_type, username, avatar_url, region, providers, onboarded, birth_month, is_15_plus, is_18_plus, last_verified_year")
      .eq("id", user.id)
      .single();
    const prem = data?.is_premium ?? false;
    setDbPremium(prem);
    setPremiumType(data?.premium_type ?? null);
    setUsername(data?.username ?? null);
    setAvatarUrl(data?.avatar_url ?? null);
    // Hydrate localStorage from DB so sync reads work everywhere
    if (data?.region) localStorage.setItem("ayeris_region", data.region);
    if (data?.providers && Array.isArray(data.providers) && data.providers.length > 0) {
      localStorage.setItem("ayeris_providers", JSON.stringify(data.providers));
    }
    if (data?.onboarded) localStorage.setItem("ayeris_onboarded", "true");
    // Hydrate age flags
    if (data?.birth_month) localStorage.setItem("ayeris_birth_month", String(data.birth_month));
    if (data?.is_15_plus !== null && data?.is_15_plus !== undefined) localStorage.setItem("ayeris_is_15_plus", String(data.is_15_plus));
    if (data?.is_18_plus !== null && data?.is_18_plus !== undefined) localStorage.setItem("ayeris_is_18_plus", String(data.is_18_plus));
    if (data?.last_verified_year) localStorage.setItem("ayeris_last_verified_year", String(data.last_verified_year));
    if (premiumOverride === null) {
      localStorage.setItem("ayeris_premium", String(prem));
    }
    // Check admin role
    const { data: roleData } = await (supabase
      .from("user_roles") as any)
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!roleData);
  };

  useEffect(() => {
    fetchProfile();
    // Check subscription status on login and periodically
    if (user) {
      checkSubscription();
      const interval = setInterval(checkSubscription, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isPremium, premiumType: effectivePremiumType, subscriptionEnd: effectiveSubscriptionEnd, cancelAtPeriodEnd, isAdmin, username, avatarUrl, refreshProfile: fetchProfile, checkSubscription, setPremiumOverride: handleSetPremiumOverride, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
